import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router';
import * as Sentry from '@sentry/react';
import { Toaster } from 'react-hot-toast';
import { useEffect } from 'react';
import AuthProvider from '@/components/auth/AuthProvider';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import AppLayout from '@/components/layout/AppLayout';
import Dashboard from '@/pages/Dashboard';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import Unauthorized from '@/pages/Unauthorized';
// Courses hidden for MVP
// import Courses from "@/pages/Courses";
// import CourseDetail from "@/pages/CourseDetail";
// import CoursePlayer from "@/pages/CoursePlayer";
import Journal from '@/pages/Journal';
import ProgressReport from '@/pages/ProgressReport';
import Progress from '@/pages/Progress';
import { MealTracking } from '@/pages/MealTracking';
import ShoppingLists from '@/pages/ShoppingLists';
import NewShoppingList from '@/pages/NewShoppingList';
import ShoppingListDetail from '@/pages/ShoppingListDetail';
import ShopForPlan from '@/pages/ShopForPlan';
import Pantry from '@/pages/Pantry';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import VerifyEmail from '@/pages/VerifyEmail';
import Activate from '@/pages/Activate';
import Profile from '@/pages/Profile';
import Settings from '@/pages/Settings';
import MyMenu from '@/pages/MyMenu';
import Recipes from '@/pages/Recipes';
import RecipeDetail from '@/pages/RecipeDetail';

// Route logger component
function RouteLogger() {
    const location = useLocation();

    useEffect(() => {
        console.log('[App] Route changed:', {
            pathname: location.pathname,
            search: location.search,
            hash: location.hash,
            timestamp: new Date().toISOString(),
        });
    }, [location]);

    return null;
}

function App() {
    return (
        <Sentry.ErrorBoundary fallback={<p>Something went wrong. Please refresh the page.</p>}>
            <AuthProvider>
                <Router>
                    <RouteLogger />
                    <Routes>
                        <Route path="/" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/verify-email" element={<VerifyEmail />} />
                        <Route path="/activate" element={<Activate />} />
                        <Route path="/unauthorized" element={<Unauthorized />} />

                        <Route
                            path="/dashboard"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <Dashboard />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />

                        {/* Courses routes hidden for MVP
          <Route
            path="/courses"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Courses />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/courses/:courseId"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <CourseDetail />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/courses/:courseId/player"
            element={
              <ProtectedRoute>
                <CoursePlayer />
              </ProtectedRoute>
            }
          />
          */}

                        <Route
                            path="/journal"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <Journal />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/progress-report"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <ProgressReport />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/progress"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <Progress />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/my-menu"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <MyMenu />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/recipes"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <Recipes />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/recipes/:id"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <RecipeDetail />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/meal-tracking"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <MealTracking />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />

                        {/* Redirect /wellness to /journal - JournalTracking page had mock data */}
                        <Route path="/wellness" element={<Navigate to="/journal" replace />} />

                        <Route
                            path="/shopping-lists"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <ShoppingLists />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/shopping-lists/new"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <NewShoppingList />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />

                        {/* One list (#330). Declared after /shopping-lists/new;
                            React Router ranks the static segment higher anyway. */}
                        <Route
                            path="/shopping-lists/:id"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <ShoppingListDetail />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />

                        {/* "Shop for your plan": pick days and meals off the plan (#330). */}
                        <Route
                            path="/shopping/plan"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <ShopForPlan />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/pantry"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <Pantry />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/profile"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <Profile />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/settings"
                            element={
                                <ProtectedRoute>
                                    <AppLayout>
                                        <Settings />
                                    </AppLayout>
                                </ProtectedRoute>
                            }
                        />

                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                    <Toaster
                        position="top-right"
                        toastOptions={{
                            duration: 4000,
                            style: {
                                background: '#363636',
                                color: '#fff',
                            },
                        }}
                    />
                </Router>
            </AuthProvider>
        </Sentry.ErrorBoundary>
    );
}

export default App;
